import { useCallback } from "react";

import { DescriptionOutlinedIcon, VisibilityOutlinedIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { openURLInNewTab } from "utils/common";

import Button from "components/Button";
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/Dialog";
import IconButton from "components/IconButton";
import Stack from "components/Stack";
import Text from "components/Text";
import Tooltip from "components/Tooltip";

import { IDetailOpenAIDocumentAssistant } from "entities/AISummaries/sdk";

export interface IFilesDialog {
  isOpen: boolean;
  files: IDetailOpenAIDocumentAssistant["files"];
  onClose: () => void;
}

const FilesDialog = ({ isOpen, files, onClose }: IFilesDialog) => {
  const handleFileDownload = useCallback(
    ({ file }) => openURLInNewTab({ url: file.url }),
    []
  );

  return (
    <Dialog open={isOpen} maxWidth="md" fullWidth onClose={onClose}>
      <DialogTitle
        sx={{
          margin: 2,
          borderRadius: "8px",
          backgroundColor: colors.blue10,
        }}
      >
        <Stack direction="row" justifyContent="space-between">
          <Stack>
            <Text
              variant="h2"
              marginBottom={0.5}
              color={colors.gray100}
              data-testid="dialog-title"
            >
              View original documents
            </Text>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ padding: 2 }}>
        <Stack alignItems="center" sx={{ maxHeight: "410px" }}>
          {files.map((file) => (
            <Stack
              key={file.id}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                width: "100%",
                padding: theme.spacing(1, 0),
                borderBottom: `1px solid ${colors.blue40}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <DescriptionOutlinedIcon />
                <Text
                  noWrap
                  variant="text3"
                  title={file.original_file_name}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 450,
                  }}
                >
                  {file.original_file_name}
                </Text>
              </Stack>
              <Tooltip title="Preview">
                <IconButton onClick={() => handleFileDownload({ file })}>
                  <VisibilityOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilesDialog;
